import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { enablePush, pushConfigured } from "@/lib/push";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BellRing, Loader2 } from "lucide-react";

export function PushEnableCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/push/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  async function on() {
    setBusy(true);
    try { await enablePush(); toast.success("Push notifications enabled on this device"); load(); } catch (e) { toast.error(e.message || formatApiError(e)); }
    setBusy(false);
  }
  const ready = pushConfigured() && st?.server_ready;
  return (
    <div data-testid="push-card" className="bg-white rounded-2xl shadow-sm p-4 mb-5 flex flex-wrap items-center gap-3">
      <BellRing size={20} className="text-violet-600" />
      <div className="flex-1 min-w-[200px]">
        <div className="font-semibold">Push notifications</div>
        <div className="text-xs text-slate-500">{!ready ? "Push alerts aren't switched on for this school yet — you'll still see everything here." : st?.registered ? `On for ${st.registered} device(s). Enable again on any new phone or browser.` : "Get alerts on this device even when the app is closed. On iPhone, add the app to your home screen first."}</div>
      </div>
      {ready && <Button data-testid="push-enable" disabled={busy} onClick={on} className="btn-brand">{busy ? <Loader2 size={15} className="animate-spin" /> : "Enable on this device"}</Button>}
    </div>
  );
}

export function PushSettingsCard() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const isAdmin = ["master_admin", "super_admin"].includes(user?.role);
  useEffect(() => { if (isAdmin) api.get("/push-settings").then((r) => setS(r.data)).catch(() => {}); }, [isAdmin]);
  if (!isAdmin || !s) return null;
  async function save(next) {
    setS(next);
    try { setS((await api.put("/push-settings", { enabled: next.enabled, events: next.events })).data); toast.success("Push settings saved"); } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div data-testid="push-settings" className="bg-white rounded-2xl shadow-sm p-5 mb-5 space-y-3">
      <div className="flex items-center gap-3"><h3 className="font-display font-bold flex-1">Push notification settings (Admin)</h3>
        <Label htmlFor="push-all" className="text-sm">All push</Label><Switch id="push-all" data-testid="push-all" checked={s.enabled} onCheckedChange={(v) => save({ ...s, enabled: v })} /></div>
      {!s.server_ready && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2">Firebase keys are not added yet, so only in-app notifications are sent. Settings below apply once push is connected.</p>}
      <div className={`grid sm:grid-cols-2 gap-x-8 gap-y-2 ${s.enabled ? "" : "opacity-50"}`}>
        {Object.entries(s.labels).map(([k, l]) => (
          <div key={k} className="flex items-center justify-between gap-3"><Label htmlFor={`push-${k}`} className="font-normal">{l}</Label>
            <Switch id={`push-${k}`} data-testid={`push-event-${k}`} disabled={!s.enabled} checked={s.events[k]} onCheckedChange={(v) => save({ ...s, events: { ...s.events, [k]: v } })} /></div>
        ))}
      </div>
      <p className="text-[11px] text-slate-400">In-app notifications are always kept in the notification centre; these switches only control phone/desktop push alerts.</p>
    </div>
  );
}
